import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, FlaskConical, KeyRound, BookOpen, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";

export const GRAPH_PERMISSIONS = [
  { role: "Organization.Read.All", why: "Tenant name and verified domains", critical: true },
  { role: "LicenseAssignment.Read.All", why: "Subscribed SKUs and seat counts", critical: true },
  { role: "User.Read.All", why: "Users and their assigned licenses", critical: true },
  { role: "AuditLog.Read.All", why: "Last sign-in per user (needs Entra ID P1/P2)", critical: false },
  { role: "Policy.Read.All", why: "Conditional Access policies for license-gap checks", critical: false },
  { role: "GroupMember.Read.All", why: "Resolve group-targeted Conditional Access policies per user", critical: false },
  { role: "Directory.Read.All", why: "Security posture: admin role memberships and legacy-auth sign-in logs", critical: false },
];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

const STEPS = [
  { title: "Create the app registration", text: "Entra admin center → Identity → Applications → App registrations → New registration. Any name (e.g. “License Hub scanner”), single tenant, no redirect URI." },
  { title: "Add Microsoft Graph application permissions", text: "API permissions → Add a permission → Microsoft Graph → Application permissions. Add the permissions listed below, then click “Grant admin consent”." },
  { title: "Create a client secret", text: "Certificates & secrets → New client secret. Copy the secret Value immediately - it is only shown once (not the Secret ID)." },
  { title: "Copy the IDs", text: "From the app’s Overview page copy the Directory (tenant) ID and the Application (client) ID and paste all three values here." },
];

export function SetupGuide({ open, onToggle }) {
  return (
    <div className={`setup-guide ${open ? "open" : ""}`} data-testid="setup-guide">
      <button type="button" className="setup-guide-toggle" onClick={onToggle} aria-expanded={open} data-testid="setup-guide-toggle">
        <BookOpen size={14} /> How to set up the Entra app registration {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="setup-guide-body">
          <ol className="setup-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="step-no">{i + 1}</span>
                <div>
                  <b>{s.title}</b>
                  <p>{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <table className="perm-table">
            <thead>
              <tr>
                <th>Application permission</th>
                <th>Used for</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {GRAPH_PERMISSIONS.map((p) => (
                <tr key={p.role}>
                  <td className="mono">{p.role}</td>
                  <td>{p.why}</td>
                  <td>{p.critical ? <span className="mini-chip warn">required</span> : <span className="mini-chip">optional</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <a className="setup-link" href="https://learn.microsoft.com/entra/identity-platform/quickstart-register-app" target="_blank" rel="noreferrer">
            Microsoft docs: register an application <ExternalLink size={12} />
          </a>
        </div>
      )}
    </div>
  );
}

export default function TenantFormDialog({ open, onOpenChange, tenant, onSaved }) {
  const editing = !!tenant;
  const [form, setForm] = useState({ name: tenant?.name || "", tenant_id: tenant?.tenant_id || "", client_id: tenant?.client_id || "", client_secret: "", sample: false });
  const [busy, setBusy] = useState(false);
  const [guide, setGuide] = useState(!editing);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const real = !form.sample && !tenant?.sample;
  const tenantIdBad = real && form.tenant_id.trim() && !(GUID.test(form.tenant_id.trim()) || DOMAIN.test(form.tenant_id.trim()));
  const clientIdBad = real && form.client_id.trim() && !GUID.test(form.client_id.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (tenantIdBad || clientIdBad) return toast.error("Check the tenant and client IDs", { description: "Both must be GUIDs (the tenant ID may also be a verified domain)." });
    setBusy(true);
    try {
      let saved;
      if (editing) {
        const body = { name: form.name };
        if (!tenant.sample) {
          if (form.tenant_id !== tenant.tenant_id) body.tenant_id = form.tenant_id.trim();
          if (form.client_id !== tenant.client_id) body.client_id = form.client_id.trim();
          if (form.client_secret) body.client_secret = form.client_secret;
        }
        saved = await authApi.tenants.patch(tenant.id, body);
        toast.success("Tenant updated");
      } else {
        saved = await authApi.tenants.create({ ...form, tenant_id: form.tenant_id.trim(), client_id: form.client_id.trim(), sample: form.sample });
        if (saved.verify?.ok) toast.success(`Connected to ${saved.org_display_name || saved.name}`);
        else toast.warning("Tenant saved but the connection failed", { description: saved.verify?.error });
      }
      setForm((f) => ({ ...f, client_secret: "" }));
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not save tenant", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl tenant-dialog" data-testid="tenant-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Microsoft tenant" : "Link a Microsoft tenant"}</DialogTitle>
            <DialogDescription>
              Paste the details of an Entra app registration with <b>application</b> permissions and admin consent. The client secret is encrypted at rest and never shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="ws-form">
            <label className="auth-field">
              <span>Display name</span>
              <Input value={form.name} onChange={set("name")} required placeholder="Contoso production" data-testid="tenant-name" />
            </label>
            {!editing && (
              <label className="ws-check" data-testid="tenant-sample-toggle">
                <input type="checkbox" checked={form.sample} onChange={(e) => setForm((f) => ({ ...f, sample: e.target.checked }))} />
                <FlaskConical size={14} /> Create a <b>sample tenant</b> with realistic demo data (no Microsoft connection)
              </label>
            )}
            {real && (
              <>
                <SetupGuide open={guide} onToggle={() => setGuide((g) => !g)} />
                <label className="auth-field">
                  <span>Directory (tenant) ID</span>
                  <Input value={form.tenant_id} onChange={set("tenant_id")} required placeholder="00000000-0000-0000-0000-000000000000 or contoso.onmicrosoft.com" aria-invalid={!!tenantIdBad} data-testid="tenant-tenant-id" />
                  {tenantIdBad && <small className="field-error" data-testid="tenant-id-error">Must be a GUID or a verified domain</small>}
                </label>
                <label className="auth-field">
                  <span>Application (client) ID</span>
                  <Input value={form.client_id} onChange={set("client_id")} required placeholder="00000000-0000-0000-0000-000000000000" aria-invalid={!!clientIdBad} data-testid="tenant-client-id" />
                  {clientIdBad && <small className="field-error" data-testid="client-id-error">Must be a GUID</small>}
                </label>
                <label className="auth-field">
                  <span>{editing ? "New client secret (leave blank to keep)" : "Client secret value"}</span>
                  <Input type="password" value={form.client_secret} onChange={set("client_secret")} required={!editing} autoComplete="off" placeholder="Secret value, not the secret ID" data-testid="tenant-client-secret" />
                </label>
                <div className="ws-hint">
                  <KeyRound size={13} /> Saving runs a connection test: we sign in with the client credentials and check which Graph permissions were granted.
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="tenant-save">
              {busy && <Loader2 size={14} className="spin" />} {editing ? "Save" : form.sample ? "Create sample tenant" : "Link & test connection"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
